import { useApi } from "@shopexperts/shared";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, type MaybeRef } from "vue";
import { unref } from "vue";

interface UseLeadsOptions {
  /** Number of items per page. Defaults to 20. */
  limit?: number;
}

export function useLeads(
  statusFilter?: MaybeRef<'pending' | 'matched' | undefined>,
  options: UseLeadsOptions = {},
) {
  const api = useApi();
  const { limit = 20 } = options;

  const status = computed(() => unref(statusFilter));

  const {
    data,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: computed(() => ['admin', 'leads-v2', 'list', { status: status.value }]),
    queryFn: async ({ pageParam }) => {
      const query: { limit?: number; cursor?: string; status?: 'pending' | 'matched' } = { limit, cursor: pageParam };
      if (status.value) {
        query.status = status.value;
      }
      return api.admin.leadsV2.listLeads({ query });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    throwOnError: true,
  });

  const leads = computed(() => data.value?.pages.flatMap((page) => page.data) || []);

  return {
    leads,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
